import { useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import styled, { CSSObject } from '@emotion/styled'
import SideModal from '../Modal/SideModal'
import {
  useModal,
  useShop,
  useConsumerCart,
  useCheckout,
  useShopPage,
  useCartMatch,
  useSessionStorage
} from 'shop/hooks'
import { useQuantityDeltas } from 'shop/hooks/useQuantityDeltas'
import ThreeDotsLoader from 'shop/components/Loader/ThreeDotsLoader'
import { TrackableEvent } from 'tracker'
import { trackUserActionsFBPixel, trackUserActionsGA4 } from 'tracker'
import { useNavigate } from 'react-router-dom'
import {
  createEcommEventDataFromConsumerCart,
  merchantGA4EcommTrackViewCart,
  slerpGA4EcommTrackViewCart
} from 'tracker/GA/ecommerce'
import { OrderItem } from 'shop/types/cart'
import { StyledHTMLElement } from 'shop/theme/types'
import { Medium } from '../common/fontSizeTags'
import ShopCartHeader from './ShopCartHeader'
import EmptyCartState from './EmptyCartState'
import ShopCartItemList from './ShopCartItemList'
import { LineBreak } from '../Shop/commonStyles'
import { AlcoholNote } from '../Cart/AlcoholNote'
import ShopCartFooter from './ShopCartFooter'
import { formatMoney } from 'shop/utils/common'
import { getAppliedRewardDiscount, getAlcoholicItems } from 'shop/utils/cart'
import { thinScrollbar } from '../common'
import { DEFAULT_NAVBAR_HEIGHT } from '../NavBar/commonStyles'
import { Discount } from '../Cart'
import { UpsellCarousel } from '../UpsellProducts/UpsellCarousel'

interface Props {
  closeCartModal?: () => void
  isModal?: boolean
}

const ShopCart = ({ closeCartModal, isModal }: Props) => {
  const { closeModal, openModal, isModalOpen } = useModal()
  const navigate = useNavigate()
  const { partner } = useShop()
  const { cart, validateCart, cartLoading } = useConsumerCart()
  const {
    minimumOrderValueWarning,
    dynamicPrepTimeSlotValidation,
    handleOrderItemQuantityChange,
    upsellProducts: contextUpsellProducts
  } = useCheckout()
  const { setProductErrorOnContinue } = useShopPage()
  const { isCartMatch } = useCartMatch()
  const { getSessionStorageItem: hasViewedUpsell } =
    useSessionStorage('upsell-modal')
  const [navBottomPosition, setNavBottomPosition] = useState('0px')
  const {
    pendingDeltas,
    updatePendingDelta,
    clearPendingDelta,
    getPendingDelta,
    resetPendingDelta
  } = useQuantityDeltas()

  const isMounted = useRef(true)
  const shopCartFooterRef = useRef<HTMLDivElement>(null)

  /** Local copy of upsell products from context  needed to support product removal animations in UpsellProductsList.
   * When a product is added to cart, it needs to stay in the DOM for ~1 second to play the fade-out/collapse animation
   * before being removed from the array. The child component filters products out after the animation completes */
  const [upsellProducts, setUpsellProducts] = useState(contextUpsellProducts)
  const [showUpsellSection, setShowUpsellSection] = useState(true)

  const orderItems = cart?.orderItems
  const hasAlcoholicProducts = useMemo(
    () => !!(orderItems && getAlcoholicItems(orderItems).length),
    [orderItems]
  )
  const hasOrderItems = !!orderItems?.length

  const showFooter = isModal ? hasOrderItems : true
  const showEmptyState = !hasOrderItems || !isCartMatch
  const showUpsellCarousel =
    isCartMatch && showUpsellSection && hasOrderItems && !!upsellProducts.length

  const appliedRewardDiscount = getAppliedRewardDiscount(
    cart?.summary.reward,
    cart?.summary.discount
  )

  /** Runs once on mount, sets isMounted & cleans up on unmount */
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  /** Calls validateCart (if there is a cart) on init to get initial errors/warnings */
  useEffect(() => {
    if (cart?.id) {
      validateCart(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id])

  /** Sync local upsell products when context changes, child UpsellCarousel component filters out added products after animations */
  useEffect(() => {
    setUpsellProducts(contextUpsellProducts)
  }, [contextUpsellProducts])

  // Ensure cart modal is below navbar on mobile
  useEffect(() => {
    if (!isModal) return

    const updateNavPosition = () => {
      const navBar = document.getElementById('nav-bar-component')
      if (navBar) {
        const rect = navBar.getBoundingClientRect()
        const bottomPosition = rect.bottom.toString()
        setNavBottomPosition(bottomPosition + 'px')
      }
    }

    // Initial position
    updateNavPosition()

    // Update on resize
    const resizeObserver = new ResizeObserver(updateNavPosition)
    const navBar = document.getElementById('nav-bar-component')
    if (navBar) {
      resizeObserver.observe(navBar)
    }

    // Cleanup resize observer
    return () => {
      resizeObserver.disconnect()
    }
  }, [isModal])

  /** Handles tracking on init */
  useEffect(() => {
    if (partner?.name && cart?.id) {
      if (!isMounted.current) return

      const { id, orderItems, store } = cart
      const { name: storeName } = store

      const eventData = {
        cart_id: id,
        products: orderItems.map((item) => ({
          product_id: item.variantId,
          name: item.name,
          currency: 'GBP',
          quantity: item.quantity,
          price: item.total.discounted || item.total.base
        }))
      }

      const ecommerceEventData = createEcommEventDataFromConsumerCart(
        cart,
        partner?.name,
        storeName
      )

      const body = {
        category: 'Cart',
        action: TrackableEvent.CartViewed
      }

      trackUserActionsGA4(body, 'slerpGA4Tracking')

      // legacy tracking
      trackUserActionsFBPixel(TrackableEvent.CartViewed, eventData)
      trackUserActionsGA4(body, 'merchantGA4Tracking')

      slerpGA4EcommTrackViewCart(ecommerceEventData)
      merchantGA4EcommTrackViewCart(ecommerceEventData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id, partner?.name])

  const checkout = () => {
    if (!isModalOpen('upsell') && !hasViewedUpsell()) {
      openModal('upsell')
      return
    }
    if (!cart?.id) return
    closeModal('cart')
    return navigate('/checkout')
  }

  const hasCheckoutWarnings =
    !!minimumOrderValueWarning || !!dynamicPrepTimeSlotValidation

  const debouncedQuantityChange = useMemo(
    () =>
      debounce(
        async (orderItem: OrderItem, proceedWithNewOrderItems?: boolean) => {
          const accumulatedDelta = getPendingDelta(orderItem.id)

          const res = await handleOrderItemQuantityChange(
            orderItem,
            accumulatedDelta,
            proceedWithNewOrderItems
          )
          const { errors } = res || {}

          if (!errors?.length) {
            clearPendingDelta(orderItem.id)
            return
          }

          // Reset pending delta to 0 when there are errors, so UI shows original quantity
          resetPendingDelta(orderItem.id, 0)

          setProductErrorOnContinue(
            () => (proceedWithNewOrderItems?: boolean) =>
              handleOrderItemQuantityChange(
                orderItem,
                accumulatedDelta,
                proceedWithNewOrderItems
              )
          )
        },
        300
      ),
    [
      getPendingDelta,
      clearPendingDelta,
      resetPendingDelta,
      handleOrderItemQuantityChange,
      setProductErrorOnContinue
    ]
  )
  /** Accumulate the delta for the specific order item, trigger debounce function */
  const handleQuantityChange = async (
    orderItem: OrderItem,
    delta: number,
    proceedWithNewOrderItems?: boolean
  ): Promise<void> => {
    updatePendingDelta(orderItem.id, delta, orderItem.quantity)

    // Trigger the debounced function
    return debouncedQuantityChange(orderItem, proceedWithNewOrderItems)
  }

  // Cleanup debounced function on teardown
  useEffect(() => {
    return () => {
      debouncedQuantityChange.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ctaCopy = () => {
    if (showEmptyState) {
      return 'Your cart is empty'
    }
    if (minimumOrderValueWarning?.amountLeftToMinimumOrderValue) {
      const amountToCheckout =
        minimumOrderValueWarning.amountLeftToMinimumOrderValue
      return `Spend ${formatMoney(amountToCheckout)} more to checkout`
    }
    if (dynamicPrepTimeSlotValidation?.restriction?.maxPossibleBasketValue) {
      const maxPossibleBasketValue =
        dynamicPrepTimeSlotValidation.restriction.maxPossibleBasketValue
      return (
        <DynamicPrepTimeCtaSpan>
          Order too large to prepare before closing.{' '}
          <Medium>
            Reduce to under {formatMoney(maxPossibleBasketValue)} to checkout.
          </Medium>
        </DynamicPrepTimeCtaSpan>
      )
    }
    return `Go to checkout`
  }

  const mainContent = (
    <>
      {showEmptyState && (
        <EmptyCartState closeModal={closeModal} isModal={!!isModal} />
      )}

      <ScrollableContentWrapper>
        {isCartMatch && (
          <>
            <AlcoholSection hasAlcoholicProducts={hasAlcoholicProducts}>
              <IdRequiredContainer>
                <AlcoholNote />
              </IdRequiredContainer>
              <LineBreakContainer>
                <LineBreak height={'2px'} />
              </LineBreakContainer>
            </AlcoholSection>

            {hasOrderItems && (
              <CartItemListContainer>
                <ShopCartItemList
                  orderItems={orderItems}
                  onQuantityChange={handleQuantityChange}
                  pendingDeltas={pendingDeltas}
                />
              </CartItemListContainer>
            )}
          </>
        )}
      </ScrollableContentWrapper>

      {showUpsellCarousel && (
        <UpsellSection>
          <FadeOverlay />
          <LineBreakContainer margin='0'>
            <LineBreak height={'2px'} />
          </LineBreakContainer>
          <UpsellHeader>
            <UpsellTitle>Top Picks Customers Love</UpsellTitle>
          </UpsellHeader>
          <UpsellCarouselContainer>
            <UpsellCarousel
              upsellProducts={upsellProducts}
              onProductsUpdate={setUpsellProducts}
              onAllProductsAdded={() => setShowUpsellSection(false)}
              shopCartFooterRef={shopCartFooterRef}
            />
          </UpsellCarouselContainer>
        </UpsellSection>
      )}
      {isCartMatch && (
        <DiscountContainer>
          <Discount
            isEditable={true}
            discount={appliedRewardDiscount}
            hasRewards={false}
          />
        </DiscountContainer>
      )}
      <StickyFooter ref={shopCartFooterRef}>
        {showFooter && (
          <ShopCartFooter
            cart={cart}
            cartLoading={cartLoading}
            hasCheckoutWarnings={hasCheckoutWarnings}
            checkout={checkout}
            ctaCopy={ctaCopy()}
            isCartMatch={isCartMatch}
          />
        )}
      </StickyFooter>
    </>
  )

  const cartContent = (
    <>
      <ShopCartHeader isModal={!!isModal} />
      <ContentSection isModal={!!isModal}>{mainContent}</ContentSection>
      {!isModal && cartLoading && (
        <LoadingContainer>
          <ThreeDotsLoader />
        </LoadingContainer>
      )}
    </>
  )

  return isModal && closeCartModal ? (
    <SideModal handleCloseModal={closeCartModal} top={navBottomPosition}>
      {cartContent}
    </SideModal>
  ) : (
    <StickyCartContainer>{cartContent}</StickyCartContainer>
  )
}

const StickyFooter = styled.div({
  position: 'sticky',
  marginTop: 'auto',
  bottom: 0,
  background: 'white',
  zIndex: 2
})

const StickyCartContainer = styled.div(({ theme }) => {
  // navbar height(64px) + fulfillment height (78px) + 16px top margin  + 16px bottom margin
  const totalOffset = DEFAULT_NAVBAR_HEIGHT + 78 + 16 + 16
  return {
    backgroundColor: 'white',
    width: '100%',
    // Full viewport height minus total offset
    height: `calc(100vh - ${totalOffset}px)`,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    overflowY: 'auto',
    position: 'relative',
    // Ensures no scroll-chaining
    overscrollBehavior: 'contain',
    boxShadow: '0px 5px 32px rgba(0, 0, 0, 0.07)',
    ...thinScrollbar(theme)
  }
})

const DynamicPrepTimeCtaSpan = styled.span<StyledHTMLElement>({
  whiteSpace: 'normal',
  textAlign: 'center'
})

const ContentSection = styled.div<StyledHTMLElement & { isModal: boolean }>(
  ({ isModal }) => ({
    padding: isModal ? '0' : '0 16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  })
)

const ScrollableContentWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0
})

const CartItemListContainer = styled.div<StyledHTMLElement>({
  marginTop: '16px'
})

const LoadingContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2
})

const AlcoholSection = styled.div<
  StyledHTMLElement & { hasAlcoholicProducts: boolean }
>(({ hasAlcoholicProducts }) => ({
  transition: 'all 0.3s ease',
  opacity: 0,
  maxHeight: 0,
  overflow: 'hidden',
  ...(hasAlcoholicProducts && {
    opacity: 1,
    maxHeight: '150px'
  })
}))

const IdRequiredContainer = styled.div({
  marginBottom: '16px'
})

const LineBreakContainer = styled.div<StyledHTMLElement & { margin?: string }>(
  ({ margin = '0 -16px 0px' }) => ({
    margin: margin
  })
)

const UpsellSection = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    position: 'relative',
    transition: 'all 0.3s ease',
    overscrollBehavior: 'contain'
  })
)

const UpsellTitle = styled.p(() => ({
  fontSize: '16px',
  fontWeight: 600,
  margin: 0
}))

const FadeOverlay = styled.div(
  (): CSSObject => ({
    position: 'absolute',
    top: '-16px',
    left: 0,
    right: 0,
    height: '16px',
    background: 'linear-gradient(to bottom, transparent, #fff)',
    pointerEvents: 'none',
    zIndex: 1
  })
)

const UpsellHeader = styled.div<StyledHTMLElement>(() => ({
  padding: '12px 0',
  display: 'flex',
  alignItems: 'center'
}))

const UpsellCarouselContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  })
)
const DiscountContainer = styled.div({
  marginTop: 'auto',
  '> div > div': {
    margin: '16px 0' // Make discount margin top/bottom smaller for shop cart
  }
})

export default ShopCart
